import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { IMAGE_LAYOUTS, MAX_IMAGE_BYTES, UNIT_LENGTH_PT, assetUri, backgroundIsDark, checkImage, clampIndent, clampMargin, cssPxToPt, ptToCssPx, rulerTicks, snapIncrementPt, snapToIncrement, toHexColor } from "@burfy/docs-core";
import { NodeSelection } from "@tiptap/pm/state";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import DOMPurify from "dompurify";
//#region src/images/AssetStoreContext.tsx
/**
* Supplies the asset store to image node views.
*
* A context rather than a prop: node views are constructed by ProseMirror, not
* by React's tree, so there is no prop chain to thread a store down.
*/
const AssetStoreContext = createContext(null);
function AssetStoreProvider({ store, children }) {
	return /* @__PURE__ */ jsx(AssetStoreContext.Provider, {
		value: store,
		children
	});
}
/** `null` when no store is configured — images then render as placeholders. */
function useAssetStore() {
	return useContext(AssetStoreContext);
}
//#endregion
//#region src/ui/ToolbarControls.tsx
function ToolbarButton({ label, onClick, active = false, disabled = false, shortcut, title, children }) {
	return /* @__PURE__ */ jsx("button", {
		type: "button",
		className: "burfy-docs-tb-button",
		"aria-pressed": active,
		"aria-label": label,
		title: title ?? (shortcut ? `${label} (${shortcut})` : label),
		disabled,
		"data-active": active || void 0,
		onMouseDown: (event) => event.preventDefault(),
		onClick,
		children
	});
}
function ToolbarSelect({ label, value, options, onChange, width }) {
	return /* @__PURE__ */ jsx("select", {
		className: "burfy-docs-tb-select",
		"aria-label": label,
		title: label,
		value,
		style: width ? { width: `${width}px` } : void 0,
		onMouseDown: (event) => event.stopPropagation(),
		onChange: (event) => onChange(event.target.value),
		children: options.map((option) => /* @__PURE__ */ jsx("option", {
			value: option.value,
			children: option.label
		}, option.value))
	});
}
function ToolbarSeparator() {
	return /* @__PURE__ */ jsx("span", {
		className: "burfy-docs-tb-separator",
		role: "separator",
		"aria-orientation": "vertical"
	});
}
function ToolbarColor({ label, value, onChange, children }) {
	return /* @__PURE__ */ jsxs("label", {
		className: "burfy-docs-tb-color",
		title: label,
		children: [/* @__PURE__ */ jsx("span", {
			"aria-hidden": "true",
			children
		}), /* @__PURE__ */ jsx("input", {
			type: "color",
			"aria-label": label,
			value,
			onChange: (event) => onChange(event.target.value)
		})]
	});
}
//#endregion
//#region src/images/import-image.ts
/**
* Bringing an image into the document.
*
* Three things happen here, and all three matter:
*
*   1. **The bytes are identified**, not the file name — see `checkImage`.
*   2. **SVG is sanitised.** An SVG is a document that can carry script and
*      remote references; rendering an unsanitised one is executing whatever
*      the author put in it.
*   3. **Large photographs are downscaled.** A 12-megapixel phone photo has no
*      business sitting in browser storage at full resolution when it renders
*      600 pixels wide.
*/
/** Longest edge kept, in device pixels. Generous for print at 300dpi. */
const MAX_DIMENSION_PX = 2400;
/** Fallback size for an SVG that declares none — the CSS default for images. */
const DEFAULT_SVG_SIZE = {
	width: 300,
	height: 150
};
var ImageImportError = class extends Error {
	reason;
	constructor(reason, message) {
		super(message);
		this.name = "ImageImportError";
		this.reason = reason;
	}
};
/**
* Work out the target size for an image, preserving its aspect ratio.
*
* Pure, so the arithmetic that decides whether a photo is resampled can be
* tested without a canvas.
*/
function planDownscale(width, height, maxDimensionPx = MAX_DIMENSION_PX) {
	const longest = Math.max(width, height);
	if (!Number.isFinite(longest) || longest <= 0) return {
		needed: false,
		width,
		height
	};
	if (longest <= maxDimensionPx) return {
		needed: false,
		width,
		height
	};
	const scale = maxDimensionPx / longest;
	return {
		needed: true,
		width: Math.max(1, Math.round(width * scale)),
		height: Math.max(1, Math.round(height * scale))
	};
}
/**
* Read an SVG's intrinsic size from its own attributes.
*
* `width`/`height` win; otherwise the `viewBox` gives the aspect ratio. An SVG
* with neither has no intrinsic size at all, so it gets the CSS default rather
* than collapsing to nothing.
*/
function readSvgSize(svg) {
	const root = new DOMParser().parseFromString(svg, "image/svg+xml").documentElement;
	/**
	* A length only counts if it is absolute.
	*
	* `width="100%"` parses as the number 100, which is not 100 pixels — it is
	* "however wide the container is", which an intrinsic size cannot express.
	* Such an SVG must fall through to its `viewBox`.
	*/
	const attr = (name) => {
		const raw = root.getAttribute(name)?.trim();
		if (!raw || !/^\d*\.?\d+(px|pt)?$/i.test(raw)) return null;
		const value = Number.parseFloat(raw);
		return Number.isFinite(value) && value > 0 ? value : null;
	};
	const width = attr("width");
	const height = attr("height");
	if (width && height) return {
		width,
		height
	};
	const viewBox = root.getAttribute("viewBox")?.trim().split(/[\s,]+/).map(Number);
	if (viewBox?.length === 4) {
		const [, , boxWidth, boxHeight] = viewBox;
		if (boxWidth && boxHeight && boxWidth > 0 && boxHeight > 0) return {
			width: boxWidth,
			height: boxHeight
		};
	}
	return DEFAULT_SVG_SIZE;
}
/** Strip everything executable or remote from an SVG. */
function sanitizeSvg(svg) {
	return DOMPurify.sanitize(svg, {
		USE_PROFILES: {
			svg: true,
			svgFilters: true
		},
		FORBID_TAGS: [
			"script",
			"foreignObject",
			"use",
			"image"
		],
		FORBID_ATTR: [
			"href",
			"xlink:href",
			"onload",
			"onerror",
			"onclick"
		]
	});
}
/** Decode an image to learn its intrinsic size. */
async function decodeSize(blob) {
	const url = URL.createObjectURL(blob);
	try {
		const image = new Image();
		await new Promise((resolve, reject) => {
			image.onload = () => resolve();
			image.onerror = () => reject(/* @__PURE__ */ new Error("decode failed"));
			image.src = url;
		});
		return {
			width: image.naturalWidth,
			height: image.naturalHeight
		};
	} finally {
		URL.revokeObjectURL(url);
	}
}
/** Resample a bitmap to the planned size. */
async function resample(blob, plan, mime) {
	if (typeof document === "undefined") return null;
	const url = URL.createObjectURL(blob);
	try {
		const image = new Image();
		await new Promise((resolve, reject) => {
			image.onload = () => resolve();
			image.onerror = () => reject(/* @__PURE__ */ new Error("decode failed"));
			image.src = url;
		});
		const canvas = document.createElement("canvas");
		canvas.width = plan.width;
		canvas.height = plan.height;
		const context = canvas.getContext("2d");
		if (!context) return null;
		context.drawImage(image, 0, 0, plan.width, plan.height);
		const resampled = await new Promise((resolve) => {
			canvas.toBlob(resolve, mime === "image/jpeg" ? "image/jpeg" : "image/png", .9);
		});
		if (!resampled) return null;
		return new Uint8Array(await resampled.arrayBuffer());
	} catch {
		return null;
	} finally {
		URL.revokeObjectURL(url);
	}
}
async function importImage(file, options = {}) {
	const { maxBytes = MAX_IMAGE_BYTES, maxDimensionPx = MAX_DIMENSION_PX } = options;
	const original = new Uint8Array(await file.arrayBuffer());
	const check = checkImage(original, maxBytes);
	if (!check.ok) throw new ImageImportError(check.rejection.reason, check.rejection.message);
	if (check.type === "svg") {
		const clean = sanitizeSvg(new TextDecoder().decode(original));
		const size = readSvgSize(clean);
		return {
			bytes: new TextEncoder().encode(clean),
			mime: check.mime,
			width: size.width,
			height: size.height
		};
	}
	const blob = new Blob([original], { type: check.mime });
	const size = await decodeSize(blob).catch(() => null);
	if (!size || size.width === 0 || size.height === 0) throw new ImageImportError("undecodable", "That image could not be opened.");
	const plan = planDownscale(size.width, size.height, maxDimensionPx);
	if (!plan.needed) return {
		bytes: original,
		mime: check.mime,
		width: size.width,
		height: size.height
	};
	const resampled = await resample(blob, plan, check.mime);
	if (!resampled) return {
		bytes: original,
		mime: check.mime,
		width: size.width,
		height: size.height
	};
	return {
		bytes: resampled,
		mime: check.mime === "image/jpeg" ? "image/jpeg" : "image/png",
		width: plan.width,
		height: plan.height
	};
}
//#endregion
//#region src/images/insert-image-files.ts
/** Is this something we should even try to import? */
function isProbablyImage(file) {
	return file.type.startsWith("image/") || /\.(png|jpe?g|gif|webp|svg)$/i.test(file.name);
}
/**
* Import one file, store its bytes, and work out the size to render it at.
*
* Shared by insertion and replacement so a replaced image is validated,
* sanitised, downscaled and fitted to the column by exactly the same rules as
* an inserted one — the alternative is two paths that agree until one of them
* is changed.
*/
async function importImageToAttributes(store, file, options = {}) {
	const image = await importImage(file);
	const asset = await store.put({
		bytes: image.bytes,
		mime: image.mime,
		width: image.width,
		height: image.height
	});
	const { maxWidthPx } = options;
	const widthPx = maxWidthPx && image.width > maxWidthPx ? Math.round(maxWidthPx) : image.width;
	const heightPx = widthPx === image.width ? image.height : Math.round(image.height / image.width * widthPx);
	return {
		src: assetUri(asset.id),
		widthPx,
		heightPx
	};
}
/**
* Swap the picture inside the selected image node.
*
* The layout and the alt text are kept. Replacing is overwhelmingly "here is a
* better version of this same picture" — a corrected chart, a re-export — and
* losing the wrap mode and the description every time would make it a chore
* rather than a convenience.
*
* The old asset's bytes are deliberately *not* removed. Undo would bring the
* node back pointing at an id that no longer resolves, and the same asset may
* be referenced by another copy of the image elsewhere in the document.
* Reclaiming them needs a sweep across every document's references, which does
* not exist yet — see the note in tasks.md rather than assuming it does.
*
* The target is resolved **before** the import, not after. Decoding a large
* photo takes long enough to click somewhere else, and a position captured
* afterwards is whatever is selected by then: a different picture gets
* replaced, or nothing does while this reports success.
*/
async function replaceSelectedImage(editor, store, file, options = {}) {
	const target = selectedImagePosition(editor);
	if (target === null) return "Select an image to replace first.";
	let attributes;
	try {
		attributes = await importImageToAttributes(store, file, options);
	} catch (error) {
		console.warn("[burfy-docs] image replace failed", error);
		return error instanceof ImageImportError ? error.message : "That image could not be added.";
	}
	const node = editor.state.doc.nodeAt(target);
	if (node?.type.name !== "image") return "That image is no longer there, so it was not replaced.";
	const tr = editor.state.tr.setNodeMarkup(target, void 0, {
		...node.attrs,
		...attributes
	});
	editor.view.dispatch(tr.setSelection(NodeSelection.create(tr.doc, target)));
	return null;
}
/** Where the selected image is, or `null` when the selection is not one. */
function selectedImagePosition(editor) {
	const { selection } = editor.state;
	if (selection instanceof NodeSelection && selection.node.type.name === "image") return selection.from;
	return null;
}
async function insertImageFiles(editor, store, files, options = {}) {
	const { maxWidthPx } = options;
	const errors = [];
	let inserted = 0;
	for (const file of files) try {
		const attributes = await importImageToAttributes(store, file, { ...maxWidthPx ? { maxWidthPx } : {} });
		editor.chain().focus().insertImage({
			...attributes,
			alt: ""
		}).run();
		inserted += 1;
	} catch (error) {
		errors.push(error instanceof ImageImportError ? error.message : "That image could not be added.");
		console.warn("[burfy-docs] image import failed", error);
	}
	return {
		inserted,
		errors
	};
}
//#endregion
//#region src/ui/Icon.tsx
/** Small, dependency-free line icons used throughout the editor chrome. */
function Icon({ name, size = 18, ...props }) {
	const common = {
		fill: "none",
		stroke: "currentColor",
		strokeWidth: 1.8,
		strokeLinecap: "round",
		strokeLinejoin: "round"
	};
	const paths = {
		"cell-properties": /* @__PURE__ */ jsxs(Fragment, { children: [
			/* @__PURE__ */ jsx("rect", {
				x: "3",
				y: "4",
				width: "18",
				height: "16",
				rx: "1.5"
			}),
			/* @__PURE__ */ jsx("path", { d: "M3 9h18M9 9v11" }),
			/* @__PURE__ */ jsx("path", {
				d: "M12 12h6v5h-6z",
				fill: "currentColor",
				stroke: "none",
				opacity: "0.35"
			})
		] }),
		"page-background": /* @__PURE__ */ jsxs(Fragment, { children: [
			/* @__PURE__ */ jsx("rect", {
				x: "4",
				y: "3",
				width: "16",
				height: "18",
				rx: "1.5"
			}),
			/* @__PURE__ */ jsx("path", { d: "M4 15l4-4 3.5 3.5L15 11l5 5" }),
			/* @__PURE__ */ jsx("circle", {
				cx: "9",
				cy: "8",
				r: "1.4"
			})
		] }),
		mic: /* @__PURE__ */ jsxs(Fragment, { children: [
			/* @__PURE__ */ jsx("rect", {
				x: "9",
				y: "3",
				width: "6",
				height: "10",
				rx: "3"
			}),
			/* @__PURE__ */ jsx("path", { d: "M5.5 11a6.5 6.5 0 0 0 13 0" }),
			/* @__PURE__ */ jsx("path", { d: "M12 17.5V21M9 21h6" })
		] }),
		undo: /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx("path", { d: "M9 7 5 11l4 4" }), /* @__PURE__ */ jsx("path", { d: "M5 11h8a5 5 0 0 1 5 5v1" })] }),
		redo: /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx("path", { d: "m15 7 4 4-4 4" }), /* @__PURE__ */ jsx("path", { d: "M19 11h-8a5 5 0 0 0-5 5v1" })] }),
		"align-left": /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx("path", { d: "M4 6h16M4 10h11M4 14h16M4 18h9" }) }),
		"align-center": /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx("path", { d: "M4 6h16M7 10h10M4 14h16M8 18h8" }) }),
		"align-right": /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx("path", { d: "M4 6h16M9 10h11M4 14h16M11 18h9" }) }),
		"align-justify": /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx("path", { d: "M4 6h16M4 10h16M4 14h16M4 18h16" }) }),
		list: /* @__PURE__ */ jsxs(Fragment, { children: [
			/* @__PURE__ */ jsx("path", { d: "M9 6h11M9 12h11M9 18h11" }),
			/* @__PURE__ */ jsx("circle", {
				cx: "5",
				cy: "6",
				r: "1",
				fill: "currentColor",
				stroke: "none"
			}),
			/* @__PURE__ */ jsx("circle", {
				cx: "5",
				cy: "12",
				r: "1",
				fill: "currentColor",
				stroke: "none"
			}),
			/* @__PURE__ */ jsx("circle", {
				cx: "5",
				cy: "18",
				r: "1",
				fill: "currentColor",
				stroke: "none"
			})
		] }),
		"list-ordered": /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx("path", { d: "M10 6h10M10 12h10M10 18h10" }), /* @__PURE__ */ jsx("path", { d: "M4 5h1v3M4 11h2l-2 3h2M4 17h2l-2 3h2" })] }),
		"check-list": /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx("path", { d: "m3 6 2 2 3-4M11 6h9M3 13l2 2 3-4M11 13h9M3 20l2 2 3-4M11 20h9" }) }),
		"indent-decrease": /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx("path", { d: "M11 6h9M11 12h9M11 18h9M8 9l-4 3 4 3" }) }),
		"indent-increase": /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx("path", { d: "M11 6h9M11 12h9M11 18h9M4 9l4 3-4 3" }) }),
		quote: /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx("path", { d: "M7 10H4a2 2 0 0 0-2 2v4h5v-4H4c0-3 1-5 3-6M18 10h-3a2 2 0 0 0-2 2v4h5v-4h-3c0-3 1-5 3-6" }) }),
		code: /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx("path", { d: "m8 9-4 3 4 3M16 9l4 3-4 3M14 5l-4 14" }) }),
		separator: /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx("path", { d: "M4 12h16" }), /* @__PURE__ */ jsx("path", { d: "m8 8 4-4 4 4M8 16l4 4 4-4" })] }),
		"page-break": /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx("path", { d: "M6 3h9l3 3v5H6zM6 21h12v-5H6zM15 3v4h3M3 13h3M9 13h2M14 13h2M19 13h2" }) }),
		"remove-format": /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx("path", { d: "m5 5 10 10M9 19h10M14 5h5M9 5h1l7 7M4 20 20 4" }) }),
		table: /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx("rect", {
			x: "3",
			y: "4",
			width: "18",
			height: "16",
			rx: "1"
		}), /* @__PURE__ */ jsx("path", { d: "M3 9h18M9 4v16M15 4v16M3 15h18" })] }),
		image: /* @__PURE__ */ jsxs(Fragment, { children: [
			/* @__PURE__ */ jsx("rect", {
				x: "3",
				y: "4",
				width: "18",
				height: "16",
				rx: "2"
			}),
			/* @__PURE__ */ jsx("circle", {
				cx: "8",
				cy: "9",
				r: "2"
			}),
			/* @__PURE__ */ jsx("path", { d: "m3 17 5-5 4 4 3-3 6 6" })
		] }),
		link: /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx("path", { d: "M10 13a5 5 0 0 0 7.5.5l2-2a5 5 0 0 0-7-7l-1.1 1.1" }), /* @__PURE__ */ jsx("path", { d: "M14 11a5 5 0 0 0-7.5-.5l-2 2a5 5 0 0 0 7 7l1.1-1.1" })] }),
		highlighter: /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx("path", { d: "m9 11 4 4 7-7-4-4zM6 14l4 4-3 3H3v-4zM3 21h18" }) }),
		upload: /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx("path", { d: "M12 16V4M7 9l5-5 5 5M5 20h14" }) }),
		"chevron-down": /* @__PURE__ */ jsx("path", { d: "m7 10 5 5 5-5" })
	};
	return /* @__PURE__ */ jsx("svg", {
		viewBox: "0 0 24 24",
		width: size,
		height: size,
		"aria-hidden": "true",
		focusable: "false",
		...common,
		...props,
		children: paths[name]
	});
}
//#endregion
//#region src/ui/ImageControls.tsx
const LAYOUT_LABEL = {
	inline: "In line with text",
	wrapLeft: "Wrap text right of image",
	wrapRight: "Wrap text left of image",
	break: "Image on its own line"
};
const LAYOUT_GLYPH = {
	inline: "⬚",
	wrapLeft: "◧",
	wrapRight: "◨",
	break: "▤"
};
/** The formats the importer accepts. Shared so the inputs cannot drift apart. */
const IMAGE_ACCEPT = "image/png,image/jpeg,image/gif,image/webp,image/svg+xml";
function ImageControls({ editor, imageSelected, activeLayout, onFilesChosen, onReplaceFile }) {
	const fileInput = useRef(null);
	const replaceInput = useRef(null);
	return /* @__PURE__ */ jsxs(Fragment, { children: [
		/* @__PURE__ */ jsx(ToolbarSeparator, {}),
		/* @__PURE__ */ jsx(ToolbarButton, {
			label: "Insert image",
			onClick: () => fileInput.current?.click(),
			children: /* @__PURE__ */ jsx(Icon, { name: "image" })
		}),
		/* @__PURE__ */ jsx("input", {
			ref: fileInput,
			type: "file",
			"aria-label": "Image file to insert",
			accept: IMAGE_ACCEPT,
			multiple: true,
			hidden: true,
			onChange: (event) => {
				const files = Array.from(event.target.files ?? []);
				event.target.value = "";
				if (files.length > 0) onFilesChosen(files);
			}
		}),
		imageSelected && /* @__PURE__ */ jsxs(Fragment, { children: [
			IMAGE_LAYOUTS.map((layout) => /* @__PURE__ */ jsx(ToolbarButton, {
				label: LAYOUT_LABEL[layout],
				active: activeLayout === layout,
				onClick: () => editor.chain().focus().setImageLayout(layout).run(),
				children: LAYOUT_GLYPH[layout]
			}, layout)),
			onReplaceFile && /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Replace image",
				onClick: () => replaceInput.current?.click(),
				children: /* @__PURE__ */ jsx(Icon, { name: "upload" })
			}), /* @__PURE__ */ jsx("input", {
				ref: replaceInput,
				type: "file",
				"aria-label": "Image file to replace with",
				accept: "image/png,image/jpeg,image/gif,image/webp,image/svg+xml",
				hidden: true,
				onChange: (event) => {
					const file = event.target.files?.[0];
					event.target.value = "";
					if (file) onReplaceFile(file);
				}
			})] }),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Reset image size",
				onClick: () => editor.chain().focus().resetImageSize().run(),
				children: "⤢"
			}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Delete image",
				onClick: () => editor.chain().focus().deleteSelection().run(),
				children: "⌫"
			}),
			/* @__PURE__ */ jsx(ToolbarButton, {
				label: "Describe image for screen readers",
				onClick: () => {
					const current = editor.getAttributes("image")["alt"];
					const next = window.prompt("Describe this image for people using a screen reader. Leave empty if it is purely decorative.", typeof current === "string" ? current : "");
					if (next !== null) editor.chain().focus().setImageAlt(next).run();
				},
				children: "alt"
			})
		] })
	] });
}
//#endregion
//#region src/ui/PageBackgroundFields.tsx
/**
* The background section of the page setup dialog.
*
* Its own component because the dialog was already long, and because the
* image branch needs the asset store, which nothing else in that form does.
*/
const FITS = [
	{
		value: "cover",
		label: "Fill the page"
	},
	{
		value: "contain",
		label: "Fit inside"
	},
	{
		value: "tile",
		label: "Tile"
	}
];
/** Starting values for each kind, so switching kind never lands on nothing. */
function defaultOf(kind) {
	switch (kind) {
		case "none": return { kind: "none" };
		case "color": return {
			kind: "color",
			color: "#f5f7fa",
			printed: true
		};
		case "gradient": return {
			kind: "gradient",
			angleDeg: 160,
			stops: [{
				color: "#eef2f7",
				positionPct: 0
			}, {
				color: "#ffffff",
				positionPct: 100
			}],
			printed: true
		};
		case "image": return {
			kind: "image",
			src: "",
			fit: "cover",
			opacity: .25,
			printed: true
		};
	}
}
function PageBackgroundFields({ value, onChange }) {
	const store = useAssetStore();
	const fileInput = useRef(null);
	const [importing, setImporting] = useState(false);
	const [error, setError] = useState(null);
	const dark = backgroundIsDark(value);
	const chooseImage = async (file) => {
		if (!store) return;
		setImporting(true);
		setError(null);
		try {
			const { src } = await importImageToAttributes(store, file);
			onChange({
				...value.kind === "image" ? value : defaultOf("image"),
				src
			});
		} catch {
			setError("That image could not be used.");
		} finally {
			setImporting(false);
		}
	};
	return /* @__PURE__ */ jsxs("fieldset", {
		className: "burfy-docs-fieldset",
		children: [
			/* @__PURE__ */ jsx("legend", { children: "Page background" }),
			/* @__PURE__ */ jsxs("label", {
				className: "burfy-docs-field",
				children: [/* @__PURE__ */ jsx("span", { children: "Type" }), /* @__PURE__ */ jsxs("select", {
					className: "burfy-docs-tb-select",
					"aria-label": "Background type",
					value: value.kind,
					onChange: (event) => onChange(defaultOf(event.target.value)),
					children: [
						/* @__PURE__ */ jsx("option", {
							value: "none",
							children: "None"
						}),
						/* @__PURE__ */ jsx("option", {
							value: "color",
							children: "Solid colour"
						}),
						/* @__PURE__ */ jsx("option", {
							value: "gradient",
							children: "Gradient"
						}),
						/* @__PURE__ */ jsx("option", {
							value: "image",
							children: "Image"
						})
					]
				})]
			}),
			value.kind === "color" && /* @__PURE__ */ jsxs("label", {
				className: "burfy-docs-field",
				children: [/* @__PURE__ */ jsx("span", { children: "Colour" }), /* @__PURE__ */ jsx("input", {
					type: "color",
					"aria-label": "Background colour",
					value: toHexColor(value.color) ?? "#ffffff",
					onChange: (event) => onChange({
						...value,
						color: event.target.value
					})
				})]
			}),
			value.kind === "gradient" && /* @__PURE__ */ jsxs(Fragment, { children: [value.stops.map((stop, index) => /* @__PURE__ */ jsxs("label", {
				className: "burfy-docs-field",
				children: [/* @__PURE__ */ jsx("span", { children: index === 0 ? "From" : "To" }), /* @__PURE__ */ jsx("input", {
					type: "color",
					"aria-label": index === 0 ? "Gradient start colour" : "Gradient end colour",
					value: toHexColor(stop.color) ?? "#ffffff",
					onChange: (event) => onChange({
						...value,
						stops: value.stops.map((existing, at) => at === index ? {
							...existing,
							color: event.target.value
						} : existing)
					})
				})]
			}, index)), /* @__PURE__ */ jsxs("label", {
				className: "burfy-docs-field",
				children: [/* @__PURE__ */ jsx("span", { children: "Angle" }), /* @__PURE__ */ jsx("input", {
					className: "burfy-docs-tb-input",
					type: "number",
					min: 0,
					max: 360,
					"aria-label": "Gradient angle in degrees",
					value: value.angleDeg,
					onChange: (event) => onChange({
						...value,
						angleDeg: Number(event.target.value)
					})
				})]
			})] }),
			value.kind === "image" && /* @__PURE__ */ jsxs(Fragment, { children: [
				/* @__PURE__ */ jsxs("div", {
					className: "burfy-docs-field",
					children: [/* @__PURE__ */ jsx("span", { children: "Image" }), /* @__PURE__ */ jsxs("span", { children: [/* @__PURE__ */ jsx("button", {
						type: "button",
						className: "burfy-docs-cell-clear",
						disabled: !store || importing,
						onClick: () => fileInput.current?.click(),
						children: importing ? "Adding…" : value.src ? "Replace…" : "Choose…"
					}), /* @__PURE__ */ jsx("input", {
						ref: fileInput,
						type: "file",
						"aria-label": "Page background image",
						accept: "image/png,image/jpeg,image/gif,image/webp,image/svg+xml",
						hidden: true,
						onChange: (event) => {
							const file = event.target.files?.[0];
							event.target.value = "";
							if (file) chooseImage(file);
						}
					})] })]
				}),
				/* @__PURE__ */ jsxs("label", {
					className: "burfy-docs-field",
					children: [/* @__PURE__ */ jsx("span", { children: "Fit" }), /* @__PURE__ */ jsx("select", {
						className: "burfy-docs-tb-select",
						"aria-label": "Background image fit",
						value: value.fit,
						onChange: (event) => onChange({
							...value,
							fit: event.target.value
						}),
						children: FITS.map((fit) => /* @__PURE__ */ jsx("option", {
							value: fit.value,
							children: fit.label
						}, fit.value))
					})]
				}),
				/* @__PURE__ */ jsxs("label", {
					className: "burfy-docs-field",
					children: [/* @__PURE__ */ jsx("span", { children: "Strength" }), /* @__PURE__ */ jsx("input", {
						type: "range",
						min: 5,
						max: 100,
						"aria-label": "Background image strength",
						value: Math.round(value.opacity * 100),
						onChange: (event) => onChange({
							...value,
							opacity: Number(event.target.value) / 100
						})
					})]
				})
			] }),
			value.kind !== "none" && /* @__PURE__ */ jsxs("label", {
				className: "burfy-docs-field",
				children: [/* @__PURE__ */ jsx("span", { children: "Print it" }), /* @__PURE__ */ jsx("input", {
					type: "checkbox",
					"aria-label": "Include the background when printing",
					checked: value.printed,
					onChange: (event) => onChange({
						...value,
						printed: event.target.checked
					})
				})]
			}),
			error && /* @__PURE__ */ jsx("p", {
				className: "burfy-docs-dialog-error",
				role: "alert",
				children: error
			}),
			dark === true && /* @__PURE__ */ jsx("p", {
				className: "burfy-docs-dialog-note",
				children: "Dark page, dark text. Change the text colour too, or it will be hard to read — this editor will not change it for you."
			}),
			value.kind === "image" && !value.src && /* @__PURE__ */ jsx("p", {
				className: "burfy-docs-dialog-note",
				children: "No image chosen yet, so nothing will be painted."
			}),
			!store && value.kind === "image" && /* @__PURE__ */ jsx("p", {
				className: "burfy-docs-dialog-note",
				children: "Images need somewhere to store their bytes, and this editor has no asset store configured."
			})
		]
	});
}
//#endregion
//#region src/ui/MenuBar.tsx
function MenuBar({ menus, children }) {
	const [open, setOpen] = useState(null);
	const barRef = useRef(null);
	const popupRef = useRef(null);
	const focusButton = useCallback((id) => {
		barRef.current?.querySelector(`[data-menu="${id}"]`)?.focus();
	}, []);
	const close = useCallback((restoreFocusTo) => {
		setOpen(null);
		if (restoreFocusTo) focusButton(restoreFocusTo);
	}, [focusButton]);
	useEffect(() => {
		if (!open) return;
		const onPointerDown = (event) => {
			const target = event.target;
			if (barRef.current?.contains(target) || popupRef.current?.contains(target)) return;
			setOpen(null);
		};
		window.document.addEventListener("pointerdown", onPointerDown, true);
		return () => {
			window.document.removeEventListener("pointerdown", onPointerDown, true);
		};
	}, [open]);
	useEffect(() => {
		if (!open?.focusFirst) return;
		itemAt(popupRef.current, 0)?.focus();
	}, [open]);
	const step = useCallback((from, delta, focusFirst) => {
		const next = menus[(menus.findIndex((menu) => menu.id === from) + delta + menus.length) % menus.length];
		if (!next) return;
		if (open) setOpen({
			id: next.id,
			focusFirst
		});
		focusButton(next.id);
	}, [
		menus,
		open,
		focusButton
	]);
	function onButtonKeyDown(event, id) {
		switch (event.key) {
			case "ArrowDown":
			case "Enter":
			case " ":
				event.preventDefault();
				setOpen({
					id,
					focusFirst: true
				});
				break;
			case "ArrowRight":
				event.preventDefault();
				step(id, 1, open !== null);
				break;
			case "ArrowLeft":
				event.preventDefault();
				step(id, -1, open !== null);
				break;
			case "Escape": close();
		}
	}
	function onPopupKeyDown(event, id) {
		const items = itemsOf(popupRef.current);
		const active = window.document.activeElement;
		const current = items.findIndex((item) => item === active);
		switch (event.key) {
			case "ArrowDown":
				event.preventDefault();
				items[(current + 1) % items.length]?.focus();
				break;
			case "ArrowUp":
				event.preventDefault();
				items[(current - 1 + items.length) % items.length]?.focus();
				break;
			case "Home":
				event.preventDefault();
				items[0]?.focus();
				break;
			case "End":
				event.preventDefault();
				items[items.length - 1]?.focus();
				break;
			case "ArrowRight":
				event.preventDefault();
				step(id, 1, true);
				break;
			case "ArrowLeft":
				event.preventDefault();
				step(id, -1, true);
				break;
			case "Escape":
				event.preventDefault();
				close(id);
				break;
			case "Tab": close();
		}
	}
	function activate(item) {
		if (item.disabled) return;
		setOpen(null);
		item.run();
	}
	const openMenu = open ? menus.find((menu) => menu.id === open.id) : void 0;
	return /* @__PURE__ */ jsxs("div", {
		className: "burfy-docs-menubar-row",
		children: [/* @__PURE__ */ jsx("div", {
			className: "burfy-docs-menubar",
			role: "menubar",
			"aria-label": "Menu",
			ref: barRef,
			children: menus.map((menu) => /* @__PURE__ */ jsxs("div", {
				className: "burfy-docs-menubar-item",
				children: [/* @__PURE__ */ jsx("button", {
					type: "button",
					className: "burfy-docs-menubar-button",
					role: "menuitem",
					"data-menu": menu.id,
					"aria-haspopup": "true",
					"aria-expanded": open?.id === menu.id,
					"data-open": open?.id === menu.id ? "" : void 0,
					onClick: () => open?.id === menu.id ? close() : setOpen({
						id: menu.id,
						focusFirst: false
					}),
					onPointerEnter: () => open && setOpen({
						id: menu.id,
						focusFirst: false
					}),
					onKeyDown: (event) => onButtonKeyDown(event, menu.id),
					children: menu.label
				}), openMenu?.id === menu.id && /* @__PURE__ */ jsx("div", {
					className: "burfy-docs-menu",
					role: "menu",
					"aria-label": menu.label,
					ref: popupRef,
					onKeyDown: (event) => onPopupKeyDown(event, menu.id),
					children: openMenu.groups.map((menuGroup, index) => /* @__PURE__ */ jsx("div", {
						className: "burfy-docs-menu-group",
						role: "group",
						"data-first": index === 0 ? "" : void 0,
						children: menuGroup.items.map((item) => /* @__PURE__ */ jsxs("button", {
							type: "button",
							className: "burfy-docs-menu-item",
							role: item.checked === void 0 ? "menuitem" : "menuitemcheckbox",
							...item.checked === void 0 ? {} : { "aria-checked": item.checked },
							disabled: item.disabled ?? false,
							tabIndex: -1,
							onClick: () => activate(item),
							children: [
								/* @__PURE__ */ jsx("span", {
									className: "burfy-docs-menu-check",
									"aria-hidden": "true",
									children: item.checked ? "✓" : ""
								}),
								/* @__PURE__ */ jsx("span", {
									className: "burfy-docs-menu-label",
									children: item.label
								}),
								item.shortcut && /* @__PURE__ */ jsx("span", {
									className: "burfy-docs-menu-shortcut",
									children: item.shortcut
								})
							]
						}, item.id))
					}, menuGroup.id))
				})]
			}, menu.id))
		}), children]
	});
}
function itemsOf(popup) {
	if (!popup) return [];
	return [...popup.querySelectorAll("button:not([disabled])")];
}
function itemAt(popup, index) {
	return itemsOf(popup)[index];
}
//#endregion
//#region src/ui/menu-model.ts
/** Drop empty groups, then menus left with nothing in them. */
function pruneMenus(menus) {
	return menus.map((menu) => ({
		...menu,
		groups: menu.groups.filter((group) => group.items.length > 0)
	})).filter((menu) => menu.groups.length > 0);
}
/**
* Merge the host app's groups into the editor's menus.
*
* App groups come **first** within a menu: File reads New, Open, Download,
* then Print, which is the order every word processor uses. A menu the editor
* does not define at all (View, typically) is created from the extras, in the
* canonical position rather than appended at the end — a menu bar whose order
* shifts with configuration is a menu bar nobody can build muscle memory for.
*/
function mergeMenus(base, extras) {
	const merged = base.map((menu) => ({
		...menu,
		groups: [...extras[menu.id] ?? [], ...menu.groups]
	}));
	const known = new Set(base.map((menu) => menu.id));
	const additions = MENU_ORDER.filter((id) => !known.has(id) && extras[id]?.length).map((id) => ({
		id,
		label: MENU_LABELS[id],
		groups: extras[id] ?? []
	}));
	return pruneMenus([...merged, ...additions].sort((left, right) => MENU_ORDER.indexOf(left.id) - MENU_ORDER.indexOf(right.id)));
}
const MENU_ORDER = [
	"file",
	"edit",
	"view",
	"insert",
	"format",
	"table",
	"help"
];
const MENU_LABELS = {
	file: "File",
	edit: "Edit",
	view: "View",
	insert: "Insert",
	format: "Format",
	table: "Table",
	help: "Help"
};
/** Convenience for building a group without repeating the shape. */
function group(id, items) {
	return {
		id,
		items: items.filter((item) => item !== null)
	};
}
//#endregion
//#region src/ui/OutlinePane.tsx
/**
* Document outline, built from the headings.
*
* Doubles as an argument for using real headings rather than bold text: a
* document that styles its sections by hand shows up here as empty, which is
* also exactly what it will look like to a screen reader.
*/
function OutlinePane({ outline, activeIndex, onSelect }) {
	return /* @__PURE__ */ jsxs("nav", {
		className: "burfy-docs-outline",
		"aria-label": "Document outline",
		children: [/* @__PURE__ */ jsx("h2", {
			className: "burfy-docs-outline-title",
			children: "Outline"
		}), outline.length === 0 ? /* @__PURE__ */ jsx("p", {
			className: "burfy-docs-outline-empty",
			children: "Headings you add will appear here."
		}) : /* @__PURE__ */ jsx("ul", { children: outline.map((entry, index) => /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx("button", {
			type: "button",
			className: "burfy-docs-outline-item",
			style: { paddingLeft: `${8 + (entry.level - 1) * 12}px` },
			"data-active": index === activeIndex || void 0,
			"aria-current": index === activeIndex ? "location" : void 0,
			onClick: () => onSelect(entry),
			children: entry.text
		}) }, `${entry.blockIndex}-${entry.text}`)) })]
	});
}
//#endregion
//#region src/ui/RulerMarker.tsx
/** Enough precision for an eighth of an inch, and no more. */
const round = (value) => Math.round(value * 1e3) / 1e3;
function RulerMarker({ shape, label, leftPx, value, min, max, unitLabel, onDrag, onStep }) {
	const dragAbort = useRef(null);
	const startDrag = useCallback((event) => {
		event.preventDefault();
		event.stopPropagation();
		const startX = event.clientX;
		dragAbort.current?.abort();
		const controller = new AbortController();
		dragAbort.current = controller;
		window.addEventListener("pointermove", (move) => onDrag(move.clientX - startX, "move"), { signal: controller.signal });
		window.addEventListener("pointerup", (up) => {
			onDrag(up.clientX - startX, "end");
			controller.abort();
			dragAbort.current = null;
		}, { signal: controller.signal });
	}, [onDrag]);
	return /* @__PURE__ */ jsx("button", {
		type: "button",
		className: "burfy-docs-ruler-marker",
		"data-shape": shape,
		style: { left: `${leftPx}px` },
		role: "slider",
		"aria-label": label,
		"aria-valuenow": round(value),
		"aria-valuemin": round(min),
		"aria-valuemax": round(max),
		"aria-valuetext": `${round(value)} ${unitLabel}`,
		onPointerDown: startDrag,
		onKeyDown: (event) => {
			const large = event.shiftKey;
			if (event.key === "ArrowLeft") {
				event.preventDefault();
				onStep(-1, large);
			} else if (event.key === "ArrowRight") {
				event.preventDefault();
				onStep(1, large);
			}
		}
	});
}
//#endregion
//#region src/ui/Ruler.tsx
const NO_INDENTS = {
	leftPt: 0,
	rightPt: 0,
	firstLinePt: 0
};
function Ruler({ geometry, zoom = 1, unit = "in", indents = NO_INDENTS, onPageSetupChange, onIndentsChange }) {
	const ticks = rulerTicks(geometry, unit);
	const increment = snapIncrementPt(unit);
	const unitPt = UNIT_LENGTH_PT[unit];
	const unitLabel = unit === "in" ? "inches" : "centimetres";
	const toPx = useCallback((pt) => ptToCssPx(pt) * zoom, [zoom]);
	/** A pixel delta on screen is a smaller distance on the page when zoomed. */
	const deltaToPt = useCallback((px) => cssPxToPt(px / zoom), [zoom]);
	/** Values at the moment a drag began, so each move is absolute, not cumulative. */
	const dragStart = useRef({
		margins: geometry.margins,
		indents
	});
	const beginDrag = useCallback(() => {
		dragStart.current = {
			margins: geometry.margins,
			indents
		};
	}, [geometry.margins, indents]);
	const applyMargin = useCallback((handle, proposedPt) => {
		const isLeft = handle === "marginLeft";
		const clamped = clampMargin({
			pageWidthPt: geometry.widthPt,
			oppositeMarginPt: isLeft ? geometry.margins.rightPt : geometry.margins.leftPt,
			proposedPt: snapToIncrement(proposedPt, increment)
		});
		onPageSetupChange?.(isLeft ? { leftPt: clamped } : { rightPt: clamped });
	}, [
		geometry,
		increment,
		onPageSetupChange
	]);
	const applyIndent = useCallback((handle, proposedPt) => {
		const snapped = snapToIncrement(proposedPt, increment);
		if (handle === "indentRight") {
			onIndentsChange?.({ rightPt: clampIndent({
				contentWidthPt: geometry.contentWidthPt,
				oppositeIndentPt: indents.leftPt,
				proposedPt: snapped
			}) });
			return;
		}
		if (handle === "firstLine") {
			const relative = snapped - indents.leftPt;
			onIndentsChange?.({ firstLinePt: relative });
			return;
		}
		const left = clampIndent({
			contentWidthPt: geometry.contentWidthPt,
			oppositeIndentPt: indents.rightPt,
			proposedPt: snapped
		});
		onIndentsChange?.({ leftPt: left });
	}, [
		geometry.contentWidthPt,
		increment,
		indents,
		onIndentsChange
	]);
	const handleDrag = useCallback((handle) => (deltaPx) => {
		const deltaPt = deltaToPt(deltaPx);
		const start = dragStart.current;
		switch (handle) {
			case "marginLeft":
				applyMargin("marginLeft", start.margins.leftPt + deltaPt);
				return;
			case "marginRight":
				applyMargin("marginRight", start.margins.rightPt - deltaPt);
				return;
			case "indentLeft":
				applyIndent("indentLeft", start.indents.leftPt + deltaPt);
				return;
			case "indentRight":
				applyIndent("indentRight", start.indents.rightPt - deltaPt);
				return;
			case "firstLine":
				applyIndent("firstLine", start.indents.leftPt + start.indents.firstLinePt + deltaPt);
				return;
		}
	}, [
		deltaToPt,
		applyMargin,
		applyIndent
	]);
	const handleStep = useCallback((handle) => (direction, large) => {
		const step = (large ? unitPt : increment) * direction;
		const margins = geometry.margins;
		switch (handle) {
			case "marginLeft":
				applyMargin("marginLeft", margins.leftPt + step);
				return;
			case "marginRight":
				applyMargin("marginRight", margins.rightPt - step);
				return;
			case "indentLeft":
				applyIndent("indentLeft", indents.leftPt + step);
				return;
			case "indentRight":
				applyIndent("indentRight", indents.rightPt - step);
				return;
			case "firstLine":
				applyIndent("firstLine", indents.leftPt + indents.firstLinePt + step);
				return;
		}
	}, [
		unitPt,
		increment,
		geometry.margins,
		indents,
		applyMargin,
		applyIndent
	]);
	const contentStartPt = geometry.margins.leftPt;
	const contentEndPt = geometry.widthPt - geometry.margins.rightPt;
	const inUnits = (pt) => pt / unitPt;
	return /* @__PURE__ */ jsx("div", {
		className: "burfy-docs-ruler-wrapper",
		onPointerDownCapture: beginDrag,
		onKeyDownCapture: beginDrag,
		children: /* @__PURE__ */ jsxs("div", {
			className: "burfy-docs-ruler",
			style: { width: `${toPx(geometry.widthPt)}px` },
			role: "group",
			"aria-label": "Page ruler",
			children: [
				/* @__PURE__ */ jsx("div", {
					className: "burfy-docs-ruler-margin",
					style: {
						left: 0,
						width: `${toPx(contentStartPt)}px`
					}
				}),
				/* @__PURE__ */ jsx("div", {
					className: "burfy-docs-ruler-margin",
					style: {
						left: `${toPx(contentEndPt)}px`,
						width: `${toPx(geometry.margins.rightPt)}px`
					}
				}),
				ticks.map((tick) => /* @__PURE__ */ jsx("span", {
					className: "burfy-docs-ruler-tick",
					"data-major": tick.major || void 0,
					style: { left: `${toPx(tick.positionPt)}px` },
					children: tick.label && /* @__PURE__ */ jsx("span", {
						className: "burfy-docs-ruler-label",
						children: tick.label
					})
				}, tick.positionPt)),
				onPageSetupChange && /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(RulerMarker, {
					shape: "margin",
					label: "Left margin",
					leftPx: toPx(contentStartPt),
					value: inUnits(geometry.margins.leftPt),
					min: 0,
					max: inUnits(geometry.widthPt - geometry.margins.rightPt),
					unitLabel,
					onDrag: handleDrag("marginLeft"),
					onStep: handleStep("marginLeft")
				}), /* @__PURE__ */ jsx(RulerMarker, {
					shape: "margin",
					label: "Right margin",
					leftPx: toPx(contentEndPt),
					value: inUnits(geometry.margins.rightPt),
					min: 0,
					max: inUnits(geometry.widthPt - geometry.margins.leftPt),
					unitLabel,
					onDrag: handleDrag("marginRight"),
					onStep: handleStep("marginRight")
				})] }),
				onIndentsChange && /* @__PURE__ */ jsxs(Fragment, { children: [
					/* @__PURE__ */ jsx(RulerMarker, {
						shape: "firstLine",
						label: "First line indent",
						leftPx: toPx(contentStartPt + indents.leftPt + indents.firstLinePt),
						value: inUnits(indents.leftPt + indents.firstLinePt),
						min: 0,
						max: inUnits(geometry.contentWidthPt),
						unitLabel,
						onDrag: handleDrag("firstLine"),
						onStep: handleStep("firstLine")
					}),
					/* @__PURE__ */ jsx(RulerMarker, {
						shape: "leftIndent",
						label: "Left indent",
						leftPx: toPx(contentStartPt + indents.leftPt),
						value: inUnits(indents.leftPt),
						min: 0,
						max: inUnits(geometry.contentWidthPt),
						unitLabel,
						onDrag: handleDrag("indentLeft"),
						onStep: handleStep("indentLeft")
					}),
					/* @__PURE__ */ jsx(RulerMarker, {
						shape: "rightIndent",
						label: "Right indent",
						leftPx: toPx(contentEndPt - indents.rightPt),
						value: inUnits(indents.rightPt),
						min: 0,
						max: inUnits(geometry.contentWidthPt),
						unitLabel,
						onDrag: handleDrag("indentRight"),
						onStep: handleStep("indentRight")
					})
				] })
			]
		})
	});
}
//#endregion
//#region src/ui/ShortcutSheet.tsx
/**
* The shortcuts worth knowing.
*
* Written with `Ctrl` and noted as `Cmd` on a Mac in one place, rather than
* detecting the platform and rewriting every row — the note is shorter than
* the code and just as clear.
*/
const GROUPS = [
	{
		title: "Text",
		shortcuts: [
			{
				keys: "Ctrl+B",
				action: "Bold"
			},
			{
				keys: "Ctrl+I",
				action: "Italic"
			},
			{
				keys: "Ctrl+U",
				action: "Underline"
			},
			{
				keys: "Ctrl+K",
				action: "Insert link"
			},
			{
				keys: "Ctrl+Shift+V",
				action: "Paste without formatting"
			}
		]
	},
	{
		title: "Paragraphs",
		shortcuts: [
			{
				keys: "Ctrl+]",
				action: "Increase indent"
			},
			{
				keys: "Ctrl+[",
				action: "Decrease indent"
			},
			{
				keys: "Ctrl+Enter",
				action: "Page break"
			}
		]
	},
	{
		title: "Document",
		shortcuts: [
			{
				keys: "Ctrl+F",
				action: "Find and replace"
			},
			{
				keys: "Ctrl+Z",
				action: "Undo"
			},
			{
				keys: "Ctrl+Shift+Z",
				action: "Redo"
			}
		]
	},
	{
		title: "Typing another language",
		shortcuts: [
			{
				keys: "Space / Enter",
				action: "Accept the highlighted suggestion"
			},
			{
				keys: "1 – 9",
				action: "Pick a suggestion by number"
			},
			{
				keys: "Esc",
				action: "Keep what you typed"
			},
			{
				keys: "Ctrl+G",
				action: "Re-open suggestions on a converted word"
			}
		]
	}
];
function ShortcutSheet({ onClose }) {
	return /* @__PURE__ */ jsx("div", {
		className: "burfy-docs-dialog-backdrop",
		onClick: onClose,
		role: "presentation",
		children: /* @__PURE__ */ jsxs("div", {
			className: "burfy-docs-dialog",
			role: "dialog",
			"aria-modal": "true",
			"aria-label": "Keyboard shortcuts",
			onClick: (event) => event.stopPropagation(),
			children: [
				/* @__PURE__ */ jsx("h2", {
					className: "burfy-docs-dialog-title",
					children: "Keyboard shortcuts"
				}),
				/* @__PURE__ */ jsx("p", {
					className: "burfy-docs-dialog-note",
					children: "On a Mac, use Cmd wherever Ctrl is shown."
				}),
				GROUPS.map((group) => /* @__PURE__ */ jsxs("section", {
					className: "burfy-docs-shortcuts",
					children: [/* @__PURE__ */ jsx("h3", { children: group.title }), /* @__PURE__ */ jsx("dl", { children: group.shortcuts.map((shortcut) => /* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsx("dt", { children: /* @__PURE__ */ jsx("kbd", { children: shortcut.keys }) }), /* @__PURE__ */ jsx("dd", { children: shortcut.action })] }, shortcut.keys)) })]
				}, group.title)),
				/* @__PURE__ */ jsxs("div", {
					className: "burfy-docs-dialog-actions",
					children: [/* @__PURE__ */ jsx("span", { className: "burfy-docs-dialog-spacer" }), /* @__PURE__ */ jsx("button", {
						type: "button",
						className: "burfy-docs-dialog-primary",
						onClick: onClose,
						children: "Close"
					})]
				})
			]
		})
	});
}
//#endregion
export { useAssetStore as C, AssetStoreProvider as S, replaceSelectedImage as _, MENU_LABELS as a, ToolbarSelect as b, mergeMenus as c, PageBackgroundFields as d, IMAGE_ACCEPT as f, isProbablyImage as g, insertImageFiles as h, OutlinePane as i, pruneMenus as l, Icon as m, Ruler as n, MENU_ORDER as o, ImageControls as p, RulerMarker as r, group as s, ShortcutSheet as t, MenuBar as u, ToolbarButton as v, ToolbarSeparator as x, ToolbarColor as y };
